import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { styled } from '@mui/material/styles';
import {
    defaultFilters,
    IJobOfferFilter,
    JobOfferFilter,
} from '@modules/JobOffers/JobOfferFilter';
import { PaginationBar } from '@components/PaginationBar';
import JobOfferList from '@modules/JobOffers/JobOfferList';
import { isDateRange, isNumberRange } from '@components/Filter/filter.utils';
import { isArray, isObject } from 'lodash';
import { DEACTIVATE_JOB_OFFER } from '@gql/jobs';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { usePageSize } from '@hooks/usePageSize';
import ConfirmationDialog from '@components/Dialog/Confirm';
import { useGetJobOffers } from '@modules/JobOffers/hooks/useGetJobOffers';
import JobOfferCreateDialogForm from '@modules/JobOffers/components/JobOfferCreateDialogForm';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SortModel } from '@components/DataTable/types';

const ContainerWrapper = styled(Box)(({ theme }) => ({
    borderRadius: '24px',
    background: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    flex: 1,
}));

const JobOfferListModule = () => {
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openModal, setOpenModal] = useState<{
        open: boolean;
        title: string;
        description: string;
        action: string;
        cancelTitle: string;
        confirmTitle: string;
    }>({
        open: false,
        title: '',
        description: '',
        action: '',
        confirmTitle: '',
        cancelTitle: '',
    });
    const [selectedId, setSelectedId] = useState<string>('');

    const [query, setQuery] = useState('');
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'job-offers-page-size' });
    const [pagination, setPagination] = useState({ page: 0, pageSize });
    const [sortBy, setSortBy] = useState({ field: 'createdDate', order: -1 });
    const [sortModel, setSortModel] = useState<SortModel[]>([]);
    const [filter, setFilter] = useState(defaultFilters);

    const changeViewMode = (event: React.MouseEvent<HTMLButtonElement>) => {
        setViewMode(event.currentTarget.value as 'grid' | 'list');
    };

    const [deactivateJobOffer] = useMutation(DEACTIVATE_JOB_OFFER);

    const updateStatusTalent = async () => {
        try {
            await deactivateJobOffer({ variables: { id: selectedId } });
            await refetch();
            showSnackbar('Talent status updated successfully', 'success');
        } catch (error) {
            showSnackbar(error.message, 'error');
        }
    };

    const handleModalAction = async () => {
        setIsLoadingSubmit(true);
        updateStatusTalent().then(() => {
            setOpenModal(prev => ({ ...prev, open: false }));
            setIsLoadingSubmit(false);
        });
    };

    const onConfirm = ({
        title,
        description,
        confirmTitle,
        cancelTitle,
        action,
    }: {
        title: string;
        description: string;
        confirmTitle: string;
        cancelTitle: string;
        action: string;
    }) => {
        setOpenModal(prev => ({ ...prev, open: true, title, description, confirmTitle, cancelTitle, action }));
    };

    const onQueryChange = useCallback((value: string) => {
        setQuery(value);
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const onSortChange = useCallback((newSortBy: { field: string; order: number }) => {
        setSortBy(newSortBy);
    }, []);

    const handleSortModelChange = useCallback((model: SortModel[]) => {
        setSortModel(model);
        const totalSort = model.find(m => m.field === 'total');
        if (totalSort?.sort === 'desc') {
            setSortBy({ field: 'total_applications', order: -1 });
        } else if (totalSort?.sort === 'asc') {
            setSortBy({ field: 'total_applications_asc', order: 1 });
        } else {
            setSortBy({ field: 'createdDate', order: -1 });
        }
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const onFilterChange = useCallback((newFilter: IJobOfferFilter) => {
        setFilter(newFilter);
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const handleDraftCreated = (jobNumber: string) => {
        setOpenCreateDialog(false);
        navigate(`/job-offers/${jobNumber}/detail`);
    };

    // Transform filter for GraphQL query
    const transformedFilter = Object.keys(filter).reduce((acc, key) => {
        const value = filter[key];
        if (key === 'jobApplicationStatus') {
            acc[key] = value.map(item => ({
                status: item.group,
                subStatus: item.value,
            }));
        } else if (key === 'specialties') {
            acc[key] = value?.map(item => item.value);
        } else if (isDateRange(value)) {
            acc[key] = {
                start: value?.start || null,
                end: value?.end || null,
            };
        } else if (isNumberRange(value)) {
            if (value?.min && value?.max) {
                acc[key] = {
                    min: value?.min ? parseFloat(value?.min) : null,
                    max: value?.max ? parseFloat(value?.max) : null,
                };
            }
        } else if (isObject(value) && 'value' in value) {
            acc[key] = value.value;
        } else if (isArray(value)) {
            if (value && value?.length > 0) {
                acc[key] = value.map(item => (item?.value ? item.value : item));
            }
        } else {
            if (value) {
                acc[key] = value;
            }
        }
        return acc;
    }, {});

    const { jobOffers, totalDocs, totalPages, loading, isLoadingMore, loadMore, error, refetch } = useGetJobOffers(
        query,
        transformedFilter,
        sortBy.field,
        pagination.page,
        pagination.pageSize,
    );

    if (error) {
        console.error('Error fetching talents:', error);
        return (
            <div>
                Error fetching talents <pre>{JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    }

    return (
        <ContainerWrapper>
            <ConfirmationDialog
                details={openModal}
                setOpen={setOpenModal}
                handleModalAction={handleModalAction}
                isLoadingSubmit={isLoadingSubmit}
            />
            <JobOfferCreateDialogForm
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                onSuccess={handleDraftCreated}
            />
            <JobOfferFilter
                viewMode={viewMode}
                changeViewMode={changeViewMode}
                totalDocs={totalDocs}
                onSortChange={onSortChange}
                onQueryChange={onQueryChange}
                onFilterChange={onFilterChange}
                onAddJobOffer={() => setOpenCreateDialog(true)}
                filter={filter}
                query={query}
            />
            <JobOfferList
                viewMode={viewMode}
                data={jobOffers}
                onConfirm={onConfirm}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
                setSelectedId={setSelectedId}
                currentPage={pagination.page + 1}
                pageSize={pagination.pageSize}
                totalRows={totalDocs}
                onPageChange={page => setPagination(prev => ({ ...prev, page: page - 1 }))}
                onPageSizeChange={limit => {
                    storePageSize(limit);
                    setPagination({ page: 0, pageSize: limit });
                }}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
            />
            {viewMode === 'grid' && (
                <PaginationBar
                    totalPages={totalPages}
                    currentPage={pagination.page + 1}
                    itemsPerPage={pagination.pageSize}
                    setCurrentPage={page => setPagination(prev => ({ ...prev, page: page - 1 }))}
                    setItemsPerPage={limit => {
                        storePageSize(limit);
                        setPagination({ page: 0, pageSize: limit });
                    }}
                />
            )}
        </ContainerWrapper>
    );
};

export default JobOfferListModule;
